import { EventPageType } from '@graphql/generated-base';
import type { PageContentType, PageKind } from '@graphql/generated-base';
import { toCanonicalMigratedSlug, isUserGeneratedMigratedSquatter, RSVP_PAGE_SLUG } from '@shared/utils/migratedCustomPages';

/**
 * Custom page slugs that use the short route format (/:eventHandle/:slug).
 * Duplicated from GuestSite.constants to avoid cross-app imports.
 * Keep in sync with ALLOWLIST_CUSTOM_PAGE_SLUGS in GuestSite.constants.ts.
 */
const ALLOWLIST_CUSTOM_PAGE_SLUGS: ReadonlySet<string> = new Set([
  'accommodations',
  'about',
  'schedule',
  'registration',
  'rsvp',
  'dress-code',
  'faqs',
  'things-to-do',
  'speakers',
  'vip',
  'weather',
]);

const CUSTOM_PAGE_PATH = 'page';

type PageInfo = Readonly<{
  type: EventPageType;
  pageSlug: string;
  pageKind?: PageKind | null;
  contentType?: PageContentType | null;
}>;

/**
 * Build the correct guest site path for a page, matching the canonical
 * routing logic in useEventPageRoute().goToPath().
 *
 * Pure function — no hooks or context required.
 *
 * @param eventHandle - The event's URL handle (e.g. "pat-and-gabe")
 * @param page - Page object with type, pageSlug, pageKind, contentType
 * @param isMigratedCustomPageEnabled - Function to check if a migrated page's feature flag is on
 * @param isDefaultPage - Whether this page is the event's default (welcome) page
 * @param options.isNewStackEvent - `useIsNewStackEvent()` for the event this page belongs to.
 *   Only a new-stack event reserves the 'rsvp' slug for its system RSVP page, so only there does a
 *   custom page at that slug take the short /<handle>/rsvp URL (MGT-176). Required so a new caller
 *   cannot silently send new-stack guests to /<handle>/page/rsvp.
 * @returns The path portion of the URL (e.g. "/pat-and-gabe/vip")
 */
export const buildGuestSitePagePath = (eventHandle: string, page: PageInfo, isDefaultPage: boolean | undefined, options: Readonly<{ isNewStackEvent: boolean }>): string => {
  if (isDefaultPage) {
    return `/${eventHandle}`;
  }

  const { type, pageSlug } = page;

  const canonicalMigratedSlug = toCanonicalMigratedSlug(pageSlug);
  const canonicalSlug = canonicalMigratedSlug ?? pageSlug;

  const isSquatter = isUserGeneratedMigratedSquatter(page);

  // Registry "Learn More" links must point at the standalone registry app route (/registry),
  // not the lean layout-engine registrylinks section. Skip user-created custom pages that merely
  // squat the registry/registrylinks slug (CORE-6456) — those render as ordinary custom pages.
  if (!isSquatter && (type === EventPageType.registry || canonicalMigratedSlug === 'registrylinks')) {
    return `/${eventHandle}/registry`;
  }

  // MGT-176: off the new stack, 'rsvp' is an ordinary host-made page and the short URL would hand
  // the guest to the legacy RSVP app instead.
  const isReservedRsvpSlug = pageSlug === RSVP_PAGE_SLUG && !options.isNewStackEvent;
  const isAllowlistedSlug = ALLOWLIST_CUSTOM_PAGE_SLUGS.has(pageSlug) && !isReservedRsvpSlug;

  // Migrated pages keep the canonical /<handle>/<slug> URL; a page the couple created has no
  // canonical slug and lives under /<handle>/page/<slug>.
  const useCustomPath = isSquatter || (type === EventPageType.custom && !isAllowlistedSlug && !canonicalMigratedSlug);

  return useCustomPath ? `/${eventHandle}/${CUSTOM_PAGE_PATH}/${pageSlug}` : `/${eventHandle}/${canonicalSlug}`;
};

/**
 * Parse a Joy guest site URL to extract the page slug, correctly handling
 * both short (/:eventHandle/:slug) and long (/:eventHandle/page/:slug) formats.
 *
 * @returns The page slug or 'welcome' for the root/default page.
 */
export const parseGuestSitePageSlug = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    const segments = parsedUrl.pathname.split('/').filter(Boolean); // ['eventHandle', 'page', 'slug'] or ['eventHandle', 'slug']

    if (segments.length < 2) {
      return 'welcome';
    }

    // Handle /page/<slug> format
    if (segments[1] === CUSTOM_PAGE_PATH && segments.length >= 3) {
      return segments[2];
    }

    return segments[1];
  } catch {
    return 'welcome';
  }
};
